import { DateTime } from "luxon";
import { classes } from "polytype";
import { omit } from "remeda";
import assertObjectFound from "../../base/assertObjectFound.js";
import {
  AggregateMetaData,
  DataModel,
  extractId,
  ListDataModel,
  ListQueryModel,
  ReferenceModel,
} from "../../base/index.js";
import { config } from "../../config/config.js";
import { Order } from "../../order/index.js";
import { Project } from "../../project/index.js";
import type { AsyncResourceVariant } from "../../react/index.js";
import { provideReact } from "../../react/index.js";
import { CertificateCheckReplaceResponse } from "../CertificateCheckReplaceResponse";
import { CertificateContact } from "../CertificateContact/CertificateContact.js";
import { CertificateRequest } from "../CertificateRequest/index.js";
import type { IngressListQuery } from "../Ingress/index.js";
import { Ingress } from "../Ingress/index.js";
import type {
  CertificateData,
  CertificateListItemData,
  CertificateListQueryModelData,
  CertificateType,
} from "./types.js";

export class Certificate extends ReferenceModel {
  public static aggregateMetaData = new AggregateMetaData("ssl", "certificate");

  public static ofId(certificateId: string) {
    return new Certificate(certificateId);
  }

  public static find = provideReact(async (id: string) => {
    const data = await config.behaviors.certificate.find(id);
    if (data !== undefined) {
      return new CertificateDetailed(data);
    }
  });

  public static get = provideReact(async (certificateId: string) => {
    const certificate = await this.find(certificateId);
    assertObjectFound(certificate, "certificate", certificateId);
    return certificate;
  });

  public findDetailed = provideReact(
    () => Certificate.find(this.id),
    [this.id],
  ) as AsyncResourceVariant<() => Promise<CertificateDetailed | undefined>>;

  public getDetailed = provideReact(
    () => Certificate.get(this.id),
    [this.id],
  ) as AsyncResourceVariant<() => Promise<CertificateDetailed>>;

  public static query(query: CertificateListQueryModelData = {}) {
    return new CertificateListQuery(query);
  }

  public async checkReplace(
    certificate: string,
    privateKey: string,
    certificateAuthority?: string,
  ): Promise<CertificateCheckReplaceResponse> {
    const response = await config.behaviors.certificate.checkReplace(
      this.id,
      certificate,
      privateKey,
      certificateAuthority,
    );
    return new CertificateCheckReplaceResponse(response);
  }

  public async replace(
    certificate: string,
    privateKey: string,
    certificateAuthority?: string,
  ) {
    await config.behaviors.certificate.replace(
      this.id,
      certificate,
      privateKey,
      certificateAuthority,
    );
  }

  public async delete() {
    await config.behaviors.certificate.delete(this.id);
  }
}

export class CertificateCommon extends classes(
  DataModel<CertificateData>,
  Certificate,
) {
  public readonly caBundle?: string;
  public readonly certificate: string;
  public readonly certificateOrder?: Order;
  public readonly certificateRequest: CertificateRequest;
  public readonly certificateType: CertificateType;
  public readonly commonName?: string;
  public readonly contact?: CertificateContact;
  public readonly dnsNames: string[];
  public readonly isExpired: boolean;
  public readonly issuer?: string;
  public readonly lastExpirationThresholdHit: number;
  public readonly project: Project;
  public readonly validFrom: DateTime;
  public readonly validTo: DateTime;
  public readonly linkedIngresses: IngressListQuery;
  public readonly name: string;
  public constructor(data: CertificateData) {
    super([data], [data.id]);
    this.caBundle = data.caBundle;
    this.certificate = data.certificate;
    if (data.certificateOrderId) {
      this.certificateOrder = Order.ofId(data.certificateOrderId);
    }
    this.certificateRequest = CertificateRequest.ofId(
      data.certificateRequestId,
    );
    this.certificateType = data.certificateType;
    this.commonName = data.commonName;
    if (data.contact) {
      this.contact = new CertificateContact(data.contact);
    }
    this.dnsNames = data.dnsNames ?? [];
    this.isExpired = data.isExpired;
    this.issuer = data.issuer;
    this.lastExpirationThresholdHit = data.lastExpirationThresholdHit;
    this.project = Project.ofId(data.projectId);
    this.validFrom = DateTime.fromISO(data.validFrom);
    this.validTo = DateTime.fromISO(data.validTo);
    this.linkedIngresses = Ingress.query({ certificateId: this.id });
    this.name = this.commonName ?? this.dnsNames[0] ?? "";
  }

  public getDisplayName = (): string => {
    return this.commonName ?? this.dnsNames[0] ?? "";
  };

  public listCompatibleIngresses = provideReact(async () => {
    return await Ingress.listCompatibleWithCertificate(
      this.project.id,
      this.certificate,
    );
  }, [this.id]);

  public isCompatibleWithHostname = (hostname: string) => {
    const itemsToCheck = this.dnsNames;
    if (this.commonName) {
      itemsToCheck.push(this.commonName);
    }

    for (const item of itemsToCheck) {
      if (item === hostname) {
        return true;
      }
      if (item.startsWith("*.")) {
        const hostnameParts = hostname.split(".");
        if (
          hostnameParts.slice(1, hostnameParts.length).join(".") ===
          item.replace("*.", "")
        ) {
          return true;
        }
      }
    }
    return false;
  };
}

export class CertificateDetailed extends classes(
  CertificateCommon,
  DataModel<CertificateData>,
) {
  public constructor(data: CertificateData) {
    super([data], [data]);
  }
}

export class CertificateListQuery extends ListQueryModel<CertificateListQueryModelData> {
  public constructor(query: CertificateListQueryModelData = {}) {
    super(query);
  }

  public refine(query: CertificateListQueryModelData = {}) {
    return new CertificateListQuery({ ...this.query, ...query });
  }

  public execute = provideReact(async () => {
    const { items, totalCount } = await config.behaviors.certificate.query({
      ...omit(this.query, ["project"]),
      projectId: extractId(this.query.project),
    });
    return new CertificateList(
      this.query,
      items.map((c) => new CertificateListItem(c)),
      totalCount,
    );
  }, [this.queryId, "certificate"]);

  public getTotalCount = provideReact(async () => {
    const { items } = await this.execute();
    return items.length;
  }, [this.queryId, "certificate"]);
}

export class CertificateListItem extends classes(
  CertificateCommon,
  DataModel<CertificateData>,
) {
  public constructor(data: CertificateListItemData) {
    super([data], [data]);
  }
}

export class CertificateList extends classes(
  CertificateListQuery,
  ListDataModel<CertificateListItem>,
) {
  public constructor(
    query: CertificateListQueryModelData,
    certificates: CertificateListItem[],
    totalCount: number,
  ) {
    super([query], [certificates, totalCount]);
  }

  public getItemsCompatibleWithHostname(hostname: string) {
    return this.items.filter((i) => i.isCompatibleWithHostname(hostname));
  }
}
